/* ═══════════════════════════════════════════════════════════════
   ProjectFormaTokens — user-defined tokens for a project's formas.
   Each token = a name + multiline text mixing free text with inserted
   data points ({Project.Number}, {Issuance.Name}, …). Placed in project
   formas as {ProjectToken:<id>}; resolved at export/preview.
   Replaces the old hardcoded "Client Project Name" field.
   ═══════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { Button } from './ui/button';

export interface ProjectFormaToken {
  id: string;
  name: string;
  content: string;
}

const INSERTS: Array<{ label: string; value: string }> = [
  { label: 'Project Name', value: '{Project.Name}' },
  { label: 'Project #', value: '{Project.Number}' },
  { label: 'Project Address', value: '{Project.Address}' },
  { label: 'Client', value: '{Client.Name}' },
  { label: 'Deliverable', value: '{Deliverable}' },
  { label: 'Issuance Name', value: '{Issuance.Name}' },
  { label: 'Issuance Date', value: '{Issuance.Date}' },
];

export function ProjectFormaTokens({
  tokens,
  onChange,
}: {
  tokens: ProjectFormaToken[];
  onChange: (next: ProjectFormaToken[]) => void;
}) {
  // Which token's insert menu is open (id) — one at a time.
  const [insertFor, setInsertFor] = useState<string | null>(null);
  const areas = React.useRef<Record<string, HTMLTextAreaElement | null>>({});

  const upd = (id: string, patch: Partial<ProjectFormaToken>) =>
    onChange(tokens.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const insertAtCursor = (t: ProjectFormaToken, ins: string) => {
    const el = areas.current[t.id];
    if (el) {
      const s = el.selectionStart ?? t.content.length;
      const e = el.selectionEnd ?? s;
      upd(t.id, { content: t.content.slice(0, s) + ins + t.content.slice(e) });
    } else {
      upd(t.id, { content: t.content + ins });
    }
    setInsertFor(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {tokens.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--tm)' }}>
          No project tokens yet. A token is a reusable line for this project's formas — e.g.{' '}
          <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 11 }}>
            RWB {'{Project.Number}'} – {'{Issuance.Name}'}
          </span>
          . Define it once, drop it into the forma, and issuance changes update it everywhere.
        </div>
      )}
      {tokens.map((t) => (
        <div
          key={t.id}
          style={{
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            background: 'var(--sf)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              value={t.name}
              placeholder="Token name (e.g. Issuance Line)"
              onChange={(e) => upd(t.id, { name: e.target.value })}
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 8px',
                border: '1px solid var(--bd)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
                color: 'var(--tx)',
              }}
            />
            <div style={{ position: 'relative' }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setInsertFor(insertFor === t.id ? null : t.id)}
              >
                Insert ▾
              </Button>
              {insertFor === t.id && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: 4,
                    zIndex: 30,
                    background: 'var(--sf)',
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-md)',
                    minWidth: 170,
                    overflow: 'hidden',
                  }}
                >
                  {INSERTS.map((ins) => (
                    <button
                      key={ins.value}
                      type="button"
                      onClick={() => insertAtCursor(t, ins.value)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '6px 10px',
                        fontSize: 12,
                        background: 'none',
                        border: 'none',
                        color: 'var(--tx)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) =>
                        ((e.target as HTMLElement).style.background = 'rgba(var(--acr),0.08)')
                      }
                      onMouseLeave={(e) => ((e.target as HTMLElement).style.background = 'none')}
                    >
                      {ins.label}
                      <span
                        style={{
                          marginLeft: 6,
                          color: 'var(--tm)',
                          fontFamily: "'JetBrains Mono'",
                          fontSize: 10,
                        }}
                      >
                        {ins.value}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Delete this token"
              onClick={() => onChange(tokens.filter((x) => x.id !== t.id))}
            >
              ✕
            </Button>
          </div>
          <textarea
            ref={(el) => {
              areas.current[t.id] = el;
            }}
            value={t.content}
            placeholder="Text with inserts — e.g. RWB {Project.Number} – {Issuance.Name}"
            onChange={(e) => upd(t.id, { content: e.target.value })}
            rows={Math.max(1, t.content.split('\n').length)}
            style={{
              fontSize: 12,
              fontFamily: "'JetBrains Mono'",
              padding: '6px 8px',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
              color: 'var(--tx)',
              resize: 'vertical',
              lineHeight: 1.5,
            }}
          />
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([...tokens, { id: 'pt_' + Date.now().toString(36), name: '', content: '' }])
          }
        >
          + Add project token
        </Button>
      </div>
    </div>
  );
}

/** Resolve a token's content against project meta: {X.Y} inserts → values. */
export function resolveProjectTokenContent(content: string, meta: Record<string, string>): string {
  return (content || '').replace(/\{([A-Za-z]+(?:\.[A-Za-z]+)?)\}/g, (_, key) => meta[key] ?? '');
}
